/**
 * PR-K — selection-window rectangle (drag-rectangle pick).
 *
 * Mirrors AutoCAD's classic two-direction selection rubber:
 *   - Left-to-right drag (mode `"window"`)   → translucent **blue**.
 *     Picks only entities **fully inside** the rectangle.
 *   - Right-to-left drag (mode `"crossing"`) → translucent **green**.
 *     Picks entities **touching** the rectangle (crossing selection).
 *
 * Coordinates arrive in CSS pixels relative to the overlay container
 * (the `<div data-testid="scene3d-overlay-portal">` from
 * `Scene3DDomOverlays`). The component derives the AABB itself —
 * callers only push `startScreen` (pointer-down) and `currentScreen`
 * (live cursor); we do the `min`/`abs` arithmetic so the rectangle
 * paints correctly regardless of drag direction.
 *
 * Visual styling lives in `styles.css` (`.scene3d-selection-window`
 * + `--window` / `--crossing` modifiers). Keeping styling in CSS lets
 * theme tweaks ride alongside the rest of the scene3d palette.
 */

import * as React from "react";

import type { SelectionWindowState } from "./overlayStateTypes";

export interface SelectionWindowRectProps {
  /** Live state slice; `null` or `visible:false` hides the overlay. */
  readonly state: SelectionWindowState | null;
}

/**
 * Pure presentation. No R3F dependencies — mounts under any HTML
 * overlay container. Returns `null` while the overlay is hidden.
 */
export function SelectionWindowRect(
  props: SelectionWindowRectProps,
): React.ReactElement | null {
  const { state } = props;
  if (!state || !state.visible) return null;

  // Compute AABB so the rectangle paints correctly for either drag
  // direction. AutoCAD always paints the box on screen even if the
  // user drags up-left or down-left — only the *colour/mode* differs.
  const left = Math.min(state.startScreen.x, state.currentScreen.x);
  const top = Math.min(state.startScreen.y, state.currentScreen.y);
  const width = Math.abs(state.currentScreen.x - state.startScreen.x);
  const height = Math.abs(state.currentScreen.y - state.startScreen.y);

  const isCrossing = state.mode === "crossing";
  const className =
    "scene3d-selection-window " +
    (isCrossing
      ? "scene3d-selection-window--crossing"
      : "scene3d-selection-window--window");

  return (
    <div
      data-testid="selection-window-rect"
      data-mode={state.mode}
      className={className}
      style={{
        position: "absolute",
        left,
        top,
        width,
        height,
        pointerEvents: "none",
        zIndex: 6,
      }}
    />
  );
}
